import { Editor } from '@tiptap/react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
	BlockquoteOutline,
	BoldOutline,
	CodeOutline,
	Divider,
	HStack,
	ItalicOutline,
	LinkAltOutline,
	ListOutline,
	OrderedListOutline,
	rem,
	StrikethroughOutline,
	StyleProps,
} from '@bonusly/design-system';

import { ControlButton } from './control-button';
import { ControlTooltip } from './control-tooltip';

interface RichTextControlsProps extends StyleProps {
	editor: Editor | null;
}

export const RichTextControls: FC<RichTextControlsProps> = ({ editor, ...rest }) => {
	const { t } = useTranslation('feedback');
	const handleSetLink = useCallback(() => {
		if (!editor) {
			return;
		}

		const previousUrl = editor.getAttributes('link').href;
		const url = window.prompt('URL', previousUrl);

		if (url === null) {
			return;
		}

		if (url === '') {
			editor.chain().focus().extendMarkRange('link').unsetLink().run();

			return;
		}

		editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
	}, [editor]);

	return (
		<HStack bg="gray.25" borderRadius="sm" h={rem(40)} p={1} spacing={1} {...rest}>
			<ControlTooltip keys="B" label={t('rich_text.bold')}>
				<ControlButton isActive={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}>
					<BoldOutline />
				</ControlButton>
			</ControlTooltip>

			<ControlTooltip keys="I" label={t('rich_text.italic')}>
				<ControlButton isActive={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}>
					<ItalicOutline />
				</ControlButton>
			</ControlTooltip>

			<ControlTooltip keys="Shift S" label={t('rich_text.strike')}>
				<ControlButton isActive={editor?.isActive('strike')} onClick={() => editor?.chain().focus().toggleStrike().run()}>
					<StrikethroughOutline />
				</ControlButton>
			</ControlTooltip>

			<Divider orientation="vertical" />

			<ControlTooltip label={t('rich_text.link')}>
				<ControlButton isActive={editor?.isActive('link')} onClick={handleSetLink}>
					<LinkAltOutline />
				</ControlButton>
			</ControlTooltip>

			<Divider orientation="vertical" />

			<ControlTooltip keys="Shift 8" label={t('rich_text.bullet_list')}>
				<ControlButton isActive={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()}>
					<ListOutline />
				</ControlButton>
			</ControlTooltip>

			<ControlTooltip keys="Shift 7" label={'Ordered list'}>
				<ControlButton isActive={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
					<OrderedListOutline />
				</ControlButton>
			</ControlTooltip>

			<Divider orientation="vertical" />

			<ControlTooltip keys="Option C" label={t('rich_text.code')}>
				<ControlButton isActive={editor?.isActive('codeBlock')} onClick={() => editor?.chain().focus().toggleCodeBlock().run()}>
					<CodeOutline />
				</ControlButton>
			</ControlTooltip>

			<ControlTooltip label={t('rich_text.quote')}>
				<ControlButton isActive={editor?.isActive('blockquote')} onClick={() => editor?.chain().focus().toggleBlockquote().run()}>
					<BlockquoteOutline />
				</ControlButton>
			</ControlTooltip>
		</HStack>
	);
};
